<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row mb-3">
      <div class="col-sm-4">
        <h4 qid="process-groups-page-title" class="mb-2">
          {{ $t('maintenance.process_groups.title') }}
        </h4>
        <div>
          <span v-if="getGuide('process_groups_list')">
            <GuideButton :guide="getGuide('process_groups_list')"></GuideButton>
          </span>
        </div>
      </div>
      <div class="col-sm-8 text-right">
        <router-link
            to="/maintenance/process-groups/create"
            class="btn btn-success float-right"
            qid="process-group-create-button"
            v-if="isGranted(permissions.PROCESS_GROUP_CREATE)"
        >
          <font-awesome-icon icon="plus"/> {{ $t('maintenance.process_groups.create_button') }}
        </router-link>
        <b-form-input
            type="text"
            v-model="filters.keywordValue"
            class="float-right mw-300 mr-3 search-bar"
            v-on:input="debounceInput"
            :placeholder="$t('maintenance.process_groups.search_placeholder')"
            qid="process-groups-search-input"
        ></b-form-input>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-10 table-container">
        <div class="card">
          <table class="table table-responsive-md">
            <thead>
            <tr>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('name')"
                  qid="sord-pg-name"
              >
                {{ $t('maintenance.process_groups.table.name') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                </span>
              </th>
              <th width="130"></th>
            </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                    {{object.name}}
                </td>
                <td align="right">
                  <span v-if="isGranted([permissions.PROCESS_GROUP_EDIT, permissions.PROCESS_GROUP_EDIT_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-edit"
                        qid="edit-pg-button"
                        @click="showEditTPModal(object)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span v-if="isGranted([permissions.PROCESS_GROUP_DELETE, permissions.PROCESS_GROUP_DELETE_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-delete"
                        qid="delete-pg-button"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    track-by="value"
                    label="value"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete third party confirm modal template -->
    <b-modal ref="delete-process-group" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-pg-title">
          {{ $t('maintenance.process_groups.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.process_groups.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.process_groups.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-pg-cancel-button"
        >
          {{ $t('maintenance.process_groups.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteTP(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-pg-submit-button"
                  v-if="isGranted([permissions.PROCESS_GROUP_DELETE, permissions.PROCESS_GROUP_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.process_groups.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit process group modal template -->
    <b-modal ref="edit-process-group" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-pg-title">
          {{ $t('maintenance.process_groups.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-pg-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editTPFormSubmit" qid="create-pg-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-pg-name-label">
              {{ $t('maintenance.process_groups.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-pg-form-name"
              type="text"
              v-model="tpForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.process_groups.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditTPModal"
                    qid="edit-pg-cancel-button"
          >
            {{ $t('maintenance.process_groups.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="edit-pg-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.process_groups.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'ProcessGroups',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.process_groups.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        tpForm: {
          name: '',
          description: '',
          classification: '',
          category: '',
          tags: '',
          client:'',
        },
        error: '',
        selectedSort: '',
        selectedDirection: '',
        filters: {
          keywordValue: ''
        },
        loading: true,
        buttonDisabled: false,
        myGuides: [],
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Maintenance',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      loadContent(page, perPage) {
        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        piincHttp.get('process-groups', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-process-group'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-process-group'].hide()
      },
      showEditTPModal(object) {
        this.tpForm.slug = object.slug;
        this.tpForm.name = object.name;
        this.$refs['edit-process-group'].show()
      },
      hideEditTPModal() {
        this.$refs['edit-process-group'].hide()
      },
      editTPFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.tpForm.client = this.getClient.slug;

        if (this.selectedTags && this.selectedTags.length) {
          for (var i = 0; i < this.selectedTags.length; i++) {
            this.tpForm.tags.push(this.selectedTags[i].name_translated);
          }
        }

        this.tpForm.classification = this.tpForm.classification ? this.tpForm.classification.value : undefined,
        this.tpForm.category = this.tpForm.category ? this.tpForm.category.category : undefined,

        piincHttp.put('process-groups/' + this.tpForm.slug, this.tpForm).then(function(response) {
          self.hideEditTPModal();
          self.buttonDisabled = false;
          self.loadContent(self.currentPage);
          self.$toastr('success', self.$t('maintenance.process_groups.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      confirmDeleteTP(object) {
        let self = this;
        piincHttp.delete('process-groups/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.process_groups.delete.success'))
        }, function() {});
      }
    },
    components: {
      GuideButton
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'process_groups',
        filters: this.filters
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.process_groups) {
          this.filters = this.getFilters.process_groups
        }
        this.loadContent();
      }
      this.loadGuides('process_groups_list');
    }
  }
</script>
